import { Injectable } from '@angular/core';
import { SpacesService, SpaceType } from 'account-hybrid/common/components/spaces';
import { DiscoveryPropertySettingState } from 'shared';
import Parse from 'v2/app/utils/parse';
import { IdentityService, License, LicenseType, SessionService } from './authentication';

export type CheckLicenseOperator = 'HAS_NOT' | 'HAS';

export enum AccountMode {
    Full = 'full',
    SpacetimeOnly = 'spacetime-only',
}

@Injectable({ providedIn: 'root' })
export class LicenseService {

    constructor(private identity: IdentityService,
        private spaceService: SpacesService,
        private sessionService: SessionService) {
    }

    checkLicense(licenses: LicenseType[], license: LicenseType, operator: CheckLicenseOperator = 'HAS') {
        if (operator === 'HAS') {
            return licenses.some(existingLicense => existingLicense === license);
        } else if (operator === 'HAS_NOT') {
            return !licenses.some(existingLicense => existingLicense === license);
        }
    }

    checkCurrentSpaceLicense(license: LicenseType, operator: CheckLicenseOperator = 'HAS') {
        if (this.identity.isAnonymous() || !this.spaceService.current) return;

        const licenses = Parse.arrayOrDefault(this.getCurrentSpaceLicenses());
        return this.checkLicense(licenses, license, operator);
    }

    /**
     * Determines whether a license should be considered active for navigation purposes.
     * - Disabled (0): not active
     * - Preview (1): active (used by Experiences)
     * - Published (2): active
     * - Hidden (3): not active for anyone
     * - FliptoOnly (4): active only for flipto staff
     */
    private isLicenseVisibleForNav(license: License): boolean {
        switch (license.IsActive) {
            case DiscoveryPropertySettingState.Published:
            case DiscoveryPropertySettingState.Preview:
                return true;
            case DiscoveryPropertySettingState.FliptoOnly:
                return this.identity.isFliptoStaff();
            case DiscoveryPropertySettingState.Hidden:
            case DiscoveryPropertySettingState.Disabled:
            default:
                return false;
        }
    }

    /**
     * Returns true if the license is explicitly set to Hidden for the current space.
     * When hidden, the license should not be shown to anyone, including flipto staff.
     */
    isCurrentSpaceLicenseHidden(license: LicenseType): boolean {
        if (this.identity.isAnonymous() || !this.spaceService.current) return false;
        const extended = this.getCurrentSpaceLicensesExtended();
        const entry = extended.find(l => l.LicenseId === license);
        return entry?.IsActive === DiscoveryPropertySettingState.Hidden;
    }

    /**
     * Mixed spaces concatenate company + property entries — pick the most
     * permissive (Published/Preview > FliptoOnly > Hidden > Disabled) so the
     * "Flip.to only" badge mirrors what the user can actually access. Without
     * this, a company-level FliptoOnly entry hides the property-level Published
     * setting and shows the badge even though Mission Control has it On.
     */
    isCurrentSpaceLicenseFliptoOnly(license: LicenseType): boolean {
        if (this.identity.isAnonymous() || !this.spaceService.current) return false;
        const entries = this.getCurrentSpaceLicensesExtended().filter(l => l.LicenseId === license);
        if (!entries.length) return false;
        if (entries.some(e => e.IsActive === DiscoveryPropertySettingState.Published
                            || e.IsActive === DiscoveryPropertySettingState.Preview)) {
            return false;
        }
        return entries.some(e => e.IsActive === DiscoveryPropertySettingState.FliptoOnly);
    }

    isCurrentSpaceLicenseDisabled(license: LicenseType): boolean {
        if (this.identity.isAnonymous() || !this.spaceService.current) return false;
        const extended = this.getCurrentSpaceLicensesExtended();
        const entry = extended.find(l => l.LicenseId === license);
        return !entry || entry.IsActive === DiscoveryPropertySettingState.Disabled;
    }

    private getCurrentSpaceLicensesExtended(): License[] {
        const space = this.spaceService.current;
        switch (space.type) {
            case SpaceType.Company:
                return this.sessionService.getCompany(this.spaceService.getCompanyUuid())?.LicensesExtended || [];
            case SpaceType.Mixed: {
                const company = this.sessionService.getCompany(this.spaceService.getCompanyUuid());
                const property = this.sessionService.getProperty(this.spaceService.getPropertyUuid());
                return [...(company?.LicensesExtended || []), ...(property?.LicensesExtended || [])];
            }
            case SpaceType.Property:
                return this.sessionService.getProperty(this.spaceService.getPropertyUuid())?.LicensesExtended || [];
            default:
                return [];
        }
    }

    getAccountMode(): AccountMode {
        if (this.identity.isFliptoStaff()) return AccountMode.Full;
        if (this.checkCurrentSpaceLicense(LicenseType.SpacetimeOnly)) return AccountMode.SpacetimeOnly;
        return AccountMode.Full;
    }

    public getCurrentSpaceLicenses() {
        const space = this.spaceService.current;
        switch (space.type) {
            case SpaceType.Company: {
                const company = this.sessionService.getCompany(this.spaceService.getCompanyUuid());
                const companyLicenses = company.LicensesExtended.filter(license => this.isLicenseVisibleForNav(license)).map(license => license.LicenseId);
                return companyLicenses || [];
            }
            case SpaceType.Mixed: {
                const company = this.sessionService.getCompany(this.spaceService.getCompanyUuid());
                const property = this.sessionService.getProperty(this.spaceService.getPropertyUuid());
                const companyLicenses = company.LicensesExtended.filter(license => this.isLicenseVisibleForNav(license)).map(license => license.LicenseId);
                const propertyLicenses = property.LicensesExtended.filter(license => this.isLicenseVisibleForNav(license)).map(license => license.LicenseId);

                return [...new Set([...(companyLicenses || []), ...(propertyLicenses || [])])];
            }
            case SpaceType.Property: {
                const property = this.sessionService.getProperty(this.spaceService.getPropertyUuid());
                return property.LicensesExtended.filter(license => this.isLicenseVisibleForNav(license)).map(license => license.LicenseId) || [];
            }
        }
    }
}
